import LoadingButton from "@mui/lab/LoadingButton"
import Button from "@mui/material/Button"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import { format as formatDate } from "date-fns"
import { useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import { useAuthorizedAxiosClient } from "../../../../common/axios"
import Column from "../../../../common/components/Column"
import LengthLimitedTextField from "../../../../common/components/LengthLimitedTextField"
import SegmentTracker from "../../../../common/tracking"
import DialogHeader from "../../components/DialogHeader"
import { useDialogContainer } from "../../utils"

export default function CreateWorkflowDialog({ open, onClose }) {
  const {
    name,
    setName,
    description,
    setDescription,
    isLoading,
    setIsLoading,
    disableSubmit,
  } = useCreateWorkflowState()
  const Container = useDialogContainer()
  const axios = useAuthorizedAxiosClient()
  const navigate = useNavigate()

  function handleClose() {
    setName("")
    setDescription("")
    onClose()
  }

  function handleCreateButtonClicked() {
    setIsLoading(true)
    axios
      .post("/workflows/api/workflows/", { name, description })
      .then(({ data }) => {
        onClose()
        SegmentTracker.track("Workflow created", {
          Name: name,
          Description: description,
        })
        navigate(`${data.id}/`)
      })
      .finally(() => setIsLoading(false))
  }

  useEffect(
    () => {
      if (!open) {
        return
      }
      setName(
        `Workflow created on ${formatDate(new Date(), "E MMM do, uuuu HH:mmb")}`
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [open]
  )

  return (
    <Container open={open} onClose={handleClose} anchor="right">
      <DialogHeader title="Create Workflow" onClose={handleClose} />
      <CreateWorkflowDialogContent
        name={name}
        setName={setName}
        description={description}
        setDescription={setDescription}
      />
      <DialogActions>
        <Button variant="outlined" color="inherit" onClick={handleClose}>
          Cancel
        </Button>
        <LoadingButton
          variant="contained"
          color="primary"
          loading={isLoading}
          disabled={disableSubmit}
          onClick={handleCreateButtonClicked}
        >
          Create
        </LoadingButton>
      </DialogActions>
    </Container>
  )
}

export function CreateWorkflowDialogContent({
  name,
  setName,
  description,
  setDescription,
}) {
  return (
    <DialogContent>
      <Column>
        <LengthLimitedTextField
          label="Name"
          value={name}
          setValue={setName}
          maxLength={128}
          required
        />
        <LengthLimitedTextField
          label="Description"
          value={description}
          setValue={setDescription}
          maxLength={180}
          multiline
        />
      </Column>
    </DialogContent>
  )
}

export function useCreateWorkflowState() {
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [isLoading, setIsLoading] = useState(false)

  const disableSubmit = !name

  return {
    name,
    setName,
    description,
    setDescription,
    isLoading,
    setIsLoading,
    disableSubmit,
  }
}
